import { useEffect } from "react";
import { ArticleQuery, AuthorBioSwanFragment, ArticleLayout } from "types/api";
import lazy from "utils/lazy";
import cx from "classnames";
import styles from "./ArticleLayoutComponent.module.scss";

import ArticleBentoRecirc from "components/article/ArticleBentoRecirc";
import ArticleWell from "components/article/ArticleWell";
import ArticleBody from "components/article/ArticleBody";

import ArticleLeadArt from "components/article/ArticleLeadArt";
import ArticleRubric from "components/article/ArticleRubric";
import ArticleTitle from "components/article/ArticleTitle";
import ArticleDek from "components/article/ArticleDek";
import ArticleBylines from "components/article/ArticleBylines";
import AboutTheAuthors from "components/article/AboutTheAuthors";
import ArticleTimestamp from "components/article/ArticleTimestamp";
import ArticleShare from "components/article/ArticleShare";
import EditorsNote from "components/article/EditorsNote";
import LeaderboardAd from "../ads/Leaderboard";
import ArticleRelatedVideos from "components/article/ArticleRelatedVideos";

const ArticleFoundationLine = lazy(
  () => import("components/article/ArticleFoundationLine")
);
const ArticleBio = lazy(() => import("components/article/ArticleBio"));
const ArticleMostPopular = lazy(
  () => import("components/article/ArticleMostPopular")
);

const ArticleLayoutComponent = (article: ArticleQuery["article"]) => {
  // add any embed script tags to the page, if needed
  useEffect(() => {
    if (article.embeds.length) {
      for (const embed of article.embeds) {
        if (embed?.type === "script") {
          const scriptEl = document.createElement("script");
          scriptEl.type = "text/javascript";
          scriptEl.src = embed.src;
          document.body.appendChild(scriptEl);
        }
      }
    }
  }, [article.embeds]);

  return (
    <main id="main-content" data-category="story page">
      {article.editorialProject &&
      article.editorialProject.river?.edges?.length > 3 ? (
        <ArticleBentoRecirc {...article.editorialProject} />
      ) : (
        // This ad and Bento should not appear at the same
        // time because together they take up 700px of space above
        // the article
        <LeaderboardAd />
      )}

      <article
        className={cx(
          styles.article,
          article.layout === ArticleLayout.Standard && styles.articleStandard
        )}
      >
        <header className={styles.articleHeader}>
          <div
            className={cx(
              article.layout === ArticleLayout.Twocol &&
                styles.twoColumnLockupWrapper
            )}
          >
            {article.layout === ArticleLayout.Feature && article.leadArt && (
              <ArticleLeadArt layout={article.layout} {...article.leadArt} />
            )}

            {/* Article Lockup Start*/}
            <div
              className={cx(
                article.layout === ArticleLayout.Twocol
                  ? styles.twoColArticleLockup
                  : styles.defaultArticleLockup
              )}
            >
              <div
                className={cx(
                  styles.rubric,
                  article.layout === ArticleLayout.Feature &&
                    styles.featureRubric,
                  article.layout === ArticleLayout.Twocol && styles.twoColRubric
                )}
              >
                <ArticleRubric {...article} />
              </div>
              <div className={styles.title}>
                <ArticleTitle {...article} />
              </div>
              <div className={styles.dek}>
                <ArticleDek {...article} />
              </div>
              <div
                className={cx(
                  styles.byline,
                  article.layout === ArticleLayout.Feature &&
                    styles.featureByline,
                  article.layout === ArticleLayout.Twocol && styles.twoColByline
                )}
              >
                <ArticleBylines {...article} />
              </div>
            </div>
            {/* Article Lockup End */}

            {(article.layout === ArticleLayout.Standard ||
              article.layout === ArticleLayout.Twocol) &&
              article.leadArt && (
                <ArticleLeadArt layout={article.layout} {...article.leadArt} />
              )}
          </div>

          {/* Article Utility Bar Start*/}
          <div className={styles.articleUtilityBar}>
            <ArticleTimestamp {...article} />
            <ArticleShare {...article} />
          </div>
          {/* Article Utility Bar End*/}

          <ArticleWell borderBottom>
            {/* Display the "About The Authors" module for non-magazine/web-only Ideas stories */}
            {article.primaryChannel?.slug === "ideas" &&
              article.__typename === "BlogArticle" && (
                <AboutTheAuthors {...article} />
              )}
            {article.editorsNote && <EditorsNote {...article.editorsNote} />}
          </ArticleWell>
        </header>

        <ArticleBody {...article} />
        <ArticleWell>
          {article.editorialProject?.foundationLineArticle && (
            <ArticleFoundationLine {...article.editorialProject} />
          )}
          {article.authors &&
            article.authors.map((author: AuthorBioSwanFragment, index) => {
              return (
                <ArticleBio
                  key={author.displayName}
                  index={index}
                  reviews={article.reviews}
                  {...author}
                />
              );
            })}
        </ArticleWell>
        <ArticleRelatedVideos
          id={article.id.split(":")[1]}
          preview={article.preview}
        />
      </article>
      <ArticleMostPopular />
    </main>
  );
};

export default ArticleLayoutComponent;
